import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { classNames } from 'primereact/utils';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useSupplierReviewCertificate } from '../../../../hooks/api/certificates';
import { useAuth } from '../../../../hooks/auth/useAuth';
import useNotificationContext from '../../../../hooks/notification/useNotificationContext';
import { PaginatedResult } from '../../../../services/AmplifyApiService';
import { ToastSeverity } from '../../../../shared/enums/toast-severity';
import {
  CertificateFeedbackStatus,
  CertificatePermissionReviewStatus,
  ICertificate,
  ISupplierCertificatesTableRow,
} from '../../../../shared/interfaces/ICertificate';

interface ISupplierCertificateReviewButtonProps {
  certificateId: string;
  className?: string;
}

const SupplierCertificateReviewButton = ({
  certificateId,
  className,
}: ISupplierCertificateReviewButtonProps): JSX.Element => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { notify } = useNotificationContext();
  const { t } = useTranslation();
  const popupTagKey = `review-button-${certificateId}`;

  const toPermissionReviewStatus = (
    clickedStatus: CertificateFeedbackStatus,
  ): CertificatePermissionReviewStatus => {
    return clickedStatus === CertificateFeedbackStatus.ACCEPTED
      ? CertificatePermissionReviewStatus.ACCEPTED
      : CertificatePermissionReviewStatus.REJECTED;
  };

  const onMutate = async ({ status }: { status: CertificateFeedbackStatus }) => {
    // Determine the next review status based on the clicked action (accept/reject)
    const nextStatus = toPermissionReviewStatus(status);

    const reviewerFirstName = user?.givenName ?? '';
    const reviewerLastName = user?.familyName ?? '';

    // Optimistically update the supplier certificates list to reflect the new review status
    queryClient.setQueriesData<PaginatedResult<ISupplierCertificatesTableRow[]>>(
      { queryKey: ['certificate/supplier'] },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          data: oldData.data.map((row) => {
            if (row.id?.toString() !== certificateId) {
              return row;
            }

            return {
              ...row,
              consumerReviewStatus: nextStatus,
              reviewedBy:
                reviewerFirstName || reviewerLastName
                  ? {
                      firstName: reviewerFirstName,
                      lastName: reviewerLastName,
                    }
                  : row.reviewedBy,
            };
          }),
        };
      },
    );

    // Also update the specific certificate details query if it's in the cache, to keep it consistent with the supplier certificates list
    queryClient.setQueryData<ICertificate | null>(['certificate', certificateId], (oldData) => {
      if (!oldData) {
        return oldData;
      }

      const reviewerFullName = `${reviewerFirstName} ${reviewerLastName}`.trim();

      return {
        ...oldData,
        consumerReviewStatus: nextStatus,
        reviewedBy: reviewerFullName,
      };
    });
  };

  const onError = () => {
    queryClient.setQueriesData<PaginatedResult<ISupplierCertificatesTableRow[]>>(
      { queryKey: ['certificate/supplier'] },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          data: oldData.data.map((row) => {
            if (row.id?.toString() !== certificateId) {
              return row;
            }

            return {
              ...row,
              reviewedBy: undefined,
            };
          }),
        };
      },
    );

    queryClient.setQueryData<ICertificate | null>(['certificate', certificateId], (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        reviewedBy: undefined,
      };
    });

    queryClient.invalidateQueries({ queryKey: ['certificate/supplier'] });
    queryClient.invalidateQueries({ queryKey: ['certificate', certificateId] });

    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.supplierCertificateReview.states.failure.detail'),
    });
  };

  const { mutate: supplierReviewCertificate, isLoading: isReviewing } =
    useSupplierReviewCertificate({
      onMutate,
      onError,
    });

  const onPopupAccept = () => {
    supplierReviewCertificate({
      certificateId,
      status: CertificateFeedbackStatus.ACCEPTED,
    });
  };

  const onPopupReject = () => {
    supplierReviewCertificate({
      certificateId,
      status: CertificateFeedbackStatus.REJECTED,
    });
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      tagKey: popupTagKey,
      target: event.currentTarget,
      acceptLabel: t('supplierCertificateDetailsPage.buttons.accept'),
      rejectLabel: t('supplierCertificateDetailsPage.buttons.reject'),
      accept: onPopupAccept,
      reject: onPopupReject,
    });
  };

  return (
    <>
      <Button
        id={`review-button-${certificateId}`}
        className={classNames('min-w-min', className)}
        label={t('certificatesPage.tabs.supplierCertificatesTab.buttons.review')}
        onClick={handleClick}
        disabled={isReviewing}
      />
      <ConfirmPopup
        tagKey={popupTagKey}
        pt={{
          content: { className: 'block p-0' },
          rejectButton: {
            root: {
              className: 'p-button-outlined',
            },
          },
        }}
      />
    </>
  );
};

export default SupplierCertificateReviewButton;
